import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  IUser,
  IUserAchievement,
  IUserBankDetails,
  IUserExperience,
  IUserLanguage,
  IUserNotifications,
  IUserPageGuides,
  IUserQualification,
  IUserSubject,
  IUserTutorDetails,
} from '.';
import APIDataProvider, { TApiResponse } from '../../api/ApiDataProvider';
import { ApiPaths } from '../../api/APIPaths';

export type TUpdateUser = {
  firstname?: string;
  lastname?: string;
  gender?: string;
  billingEmail?: string;
  dateOfBirth?: string;
  email?: string;
  phoneNumber?: string;
  country?: string;
  curriculum?: string;
  grade?: string;
  avatar?: string;
  examDate?: string;
  schoolName?: string;
  parentName?: string;
  parentEmail?: string;
  parentPhoneNumber?: string;
  nationality?: string;
  emergencyNumber?: string;
  notifications?: IUserNotifications;
  tutorDetails?: IUserTutorDetails;
  bankDetails?: IUserBankDetails;
  pageGuides?: IUserPageGuides;
  msTeamsEmail?: string;
  feedbackPreferences?: {
    date: string;
    options: string[];
    other?: string;
  };
};

type TDeleteUser = {
  password: string;
};

const apiDataProvider = new APIDataProvider();

export const getUser = createAsyncThunk('/user/get', async (userId: string) => {
  const path = `${ApiPaths.user}/${userId}`;
  const response = await apiDataProvider.get<TApiResponse<IUser>>(path);

  return response.data;
});

export const updateUser = createAsyncThunk(
  '/user/update',
  async (data: TUpdateUser) => {
    const path = ApiPaths.user;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const deleteUser = createAsyncThunk(
  '/user/delete',
  async (data: TDeleteUser) => {
    const path = `${ApiPaths.user}/delete`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const addTutorExperience = createAsyncThunk(
  '/user/experience/add',
  async (data: IUserExperience) => {
    const path = `${ApiPaths.user}/experiences`;
    const response = await apiDataProvider.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorExperience = createAsyncThunk(
  '/user/experience/update',
  async (data: IUserExperience) => {
    const path = `${ApiPaths.user}/experiences`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const deleteTutorExperience = createAsyncThunk(
  '/user/experience/delete',
  async (id: string) => {
    const path = `${ApiPaths.user}/experiences/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addTutorQualification = createAsyncThunk(
  '/user/qualifications/add',
  async (data: IUserQualification) => {
    const path = `${ApiPaths.user}/qualifications`;
    const response = await apiDataProvider.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateTutorQualification = createAsyncThunk(
  '/user/qualifications/update',
  async (data: IUserQualification) => {
    const path = `${ApiPaths.user}/qualifications`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const deleteTutorQualification = createAsyncThunk(
  '/user/qualifications/delete',
  async (id: string) => {
    const path = `${ApiPaths.user}/qualifications/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addUserAchievement = createAsyncThunk(
  '/user/achievements/add',
  async (data: IUserAchievement) => {
    const path = `${ApiPaths.user}/achievements`;
    const response = await apiDataProvider.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateUserAchievement = createAsyncThunk(
  '/user/achievements/update',
  async (data: IUserAchievement) => {
    const path = `${ApiPaths.user}/achievements`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const deleteUserAchievement = createAsyncThunk(
  '/user/achievements/delete',
  async (id: string) => {
    const path = `${ApiPaths.user}/achievements/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addUserLanguage = createAsyncThunk(
  '/user/languages/add',
  async (data: IUserLanguage) => {
    const path = `${ApiPaths.user}/languages`;
    const response = await apiDataProvider.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateUserLanguage = createAsyncThunk(
  '/user/languages/update',
  async (data: IUserLanguage) => {
    const path = `${ApiPaths.user}/languages`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);

export const deleteUserLanguage = createAsyncThunk(
  '/user/languages/delete',
  async (id: string) => {
    const path = `${ApiPaths.user}/languages/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const addUserSubject = createAsyncThunk(
  '/user/subjects/add',
  async (data: IUserSubject) => {
    const path = `${ApiPaths.user}/subjects`;
    const response = await apiDataProvider.post<TApiResponse<IUser>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteUserSubject = createAsyncThunk(
  '/user/subjects/delete',
  async (id: string) => {
    const path = `${ApiPaths.user}/subjects/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<IUser>>(path);

    return response.data;
  }
);

export const getEnrolmentCourseRelatedStudents = createAsyncThunk(
  '/user/enrolment-course-students',
  async () => {
    const path = `${ApiPaths.user}/enrolment-course-students`;
    const response = await apiDataProvider.get<TApiResponse<IUser[]>>(path);

    return response.data;
  }
);

export const getEnrolmentCourseRelatedTutors = createAsyncThunk(
  '/user/enrolment-course-tutors',
  async () => {
    const path = `${ApiPaths.user}/enrolment-course-tutors`;
    const response = await apiDataProvider.get<TApiResponse<IUser[]>>(path);

    return response.data;
  }
);

export const updateUserPageGuides = createAsyncThunk(
  '/user/update-page-guides',
  async (data: { pageGuides: IUserPageGuides }) => {
    const path = `${ApiPaths.user}/page-guides`;
    const response = await apiDataProvider.put<TApiResponse<IUser>>(path, data);

    return response.data;
  }
);
